import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../utils/api';

type LineItem = {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
};

type QuoteData = {
  quote: {
    id: string;
    quoteNumber: string;
    jobTitle: string;
    description?: string | null;
    amount: number;
    vatAmount: number;
    totalAmount: number;
    status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CHANGES_REQUESTED';
    validUntil: string;
    notes?: string | null;
    siteAddress?: string | null;
    sitePostcode?: string | null;
    createdAt: string;
    items: LineItem[];
  };
  client: {
    name: string;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    postcode?: string | null;
  } | null;
  company: {
    name?: string | null;
    address?: string | null;
    phone?: string | null;
    businessEmail?: string | null;
    logo?: string | null;
    vatNumber?: string | null;
  } | null;
};

type View = 'loading' | 'ready' | 'approving' | 'approved' | 'rejecting' | 'rejected' | 'requesting' | 'requested' | 'error' | 'notfound';

const money = (n: number | undefined) => `£${Number(n || 0).toFixed(2)}`;
const formatDate = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function PublicQuote() {
  const { token = '' } = useParams<{ token: string }>();
  const [view, setView] = useState<View>('loading');
  const [data, setData] = useState<QuoteData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');

  // Modal state
  const [modal, setModal] = useState<null | 'approve' | 'reject' | 'changes'>(null);
  const [formName, setFormName] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const pdfUrl = useMemo(() => `${API_BASE_URL}/public/quotes/${token}/pdf`, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/public/quotes/${token}`);
        if (res.status === 404) {
          if (!cancelled) setView('notfound');
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (cancelled) return;
        setData(json.data);
        const status = json.data?.quote?.status;
        if (status === 'ACCEPTED') setView('approved');
        else if (status === 'REJECTED') setView('rejected');
        else if (status === 'CHANGES_REQUESTED') setView('requested');
        else setView('ready');
      } catch (err: any) {
        if (cancelled) return;
        setErrorMsg(err?.message || 'Failed to load quote');
        setView('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const postAction = async (action: 'approve' | 'reject' | 'request-changes', body: any) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/public/quotes/${token}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      if (action === 'approve') setView('approved');
      else if (action === 'reject') setView('rejected');
      else setView('requested');
      setModal(null);
      setFormName('');
      setFormMessage('');
    } catch (err: any) {
      alert(`Something went wrong: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (view === 'loading') {
    return <Centered><p>Loading your quote…</p></Centered>;
  }
  if (view === 'notfound') {
    return <Centered><h1>Quote not found</h1><p>This link may have expired or been revoked. Please contact the sender.</p></Centered>;
  }
  if (view === 'error') {
    return <Centered><h1>Something went wrong</h1><p>{errorMsg}</p></Centered>;
  }
  if (!data) return null;

  const { quote, client, company } = data;
  const isTerminal = view === 'approved' || view === 'rejected';
  const companyName = company?.name || 'Your contractor';

  return (
    <div style={styles.page}>
      <div style={styles.wrap}>
        {/* Header */}
        <header style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {company?.logo ? (
              <img src={company.logo} alt={companyName} style={{ height: 40, width: 40, objectFit: 'contain', borderRadius: 6 }} />
            ) : null}
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{companyName}</div>
              {company?.businessEmail && <div style={{ color: '#6b7280', fontSize: 12 }}>{company.businessEmail}</div>}
            </div>
          </div>
          <StatusBadge status={quote.status} />
        </header>

        {/* Summary card */}
        <section style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={styles.label}>Quote</div>
              <h1 style={{ margin: '4px 0 0', fontSize: 28, color: '#111827' }}>{quote.quoteNumber}</h1>
              <div style={{ color: '#374151', marginTop: 6 }}>{quote.jobTitle}</div>
              <div style={{ color: '#6b7280', fontSize: 13, marginTop: 10 }}>
                Issued {formatDate(quote.createdAt)} · Valid until {formatDate(quote.validUntil)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={styles.label}>Total</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#111827', marginTop: 4 }}>{money(quote.totalAmount)}</div>
              <div style={{ color: '#6b7280', fontSize: 13 }}>
                Subtotal {money(quote.amount)} · VAT {money(quote.vatAmount)}
              </div>
            </div>
          </div>

          {client && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              <div>
                <div style={styles.label}>Billed to</div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>{client.name}</div>
                {client.address && <div style={{ color: '#6b7280', fontSize: 13 }}>{client.address}</div>}
                {client.postcode && <div style={{ color: '#6b7280', fontSize: 13 }}>{client.postcode}</div>}
              </div>
              {(quote.siteAddress || quote.sitePostcode) && (
                <div>
                  <div style={styles.label}>Site</div>
                  <div style={{ marginTop: 4 }}>{quote.siteAddress}</div>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>{quote.sitePostcode}</div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Description */}
        {quote.description && (
          <section style={styles.card}>
            <div style={styles.label}>Description</div>
            <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', color: '#374151' }}>{quote.description}</p>
          </section>
        )}

        {/* Line items */}
        {quote.items?.length > 0 && (
          <section style={styles.card}>
            <div style={styles.label}>Line items</div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Description</th>
                  <th style={{ ...styles.th, textAlign: 'right', width: 60 }}>Qty</th>
                  <th style={{ ...styles.th, textAlign: 'right', width: 100 }}>Unit</th>
                  <th style={{ ...styles.th, textAlign: 'right', width: 100 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {quote.items.map((it) => (
                  <tr key={it.id}>
                    <td style={styles.td}>{it.description}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{it.quantity}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{money(it.unitPrice)}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{money(it.totalPrice)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Notes */}
        {quote.notes && (
          <section style={styles.card}>
            <div style={styles.label}>Notes</div>
            <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', color: '#374151' }}>{quote.notes}</p>
          </section>
        )}

        {/* PDF + Actions */}
        <section style={styles.card}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <a href={pdfUrl} target="_blank" rel="noreferrer" style={styles.secondaryBtn}>
              📄 View PDF
            </a>
            {!isTerminal && view !== 'requested' && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={() => setModal('changes')} style={styles.secondaryBtn}>Request changes</button>
                <button onClick={() => setModal('reject')} style={{ ...styles.secondaryBtn, color: '#b91c1c' }}>Reject</button>
                <button onClick={() => setModal('approve')} style={styles.primaryBtn}>Approve quote</button>
              </div>
            )}
          </div>
        </section>

        {/* Terminal state banners */}
        {view === 'approved' && <Banner tone="success" title="Quote approved — thanks!" body={`Thanks ${quote['approvedByClientName' as keyof typeof quote] as any || ''}! ${companyName} has been notified and will be in touch to schedule the work.`} />}
        {view === 'rejected' && <Banner tone="muted" title="Quote rejected" body={`${companyName} has been notified.`} />}
        {view === 'requested' && <Banner tone="info" title="Changes requested" body={`${companyName} has been notified and will review your feedback.`} />}

        <footer style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '24px 0' }}>
          Sent securely via Certs · {companyName}
        </footer>
      </div>

      {/* Modals */}
      {modal === 'approve' && (
        <Modal title="Approve quote" onClose={() => setModal(null)}>
          <p style={{ marginTop: 0, color: '#374151' }}>You're approving <strong>{quote.quoteNumber}</strong> for <strong>{money(quote.totalAmount)}</strong>.</p>
          <Field label="Your name (for the record)">
            <input style={styles.input} value={formName} onChange={(e) => setFormName(e.target.value)} placeholder={client?.name || 'Your name'} />
          </Field>
          <Field label="Optional message">
            <textarea style={{ ...styles.input, minHeight: 80 }} value={formMessage} onChange={(e) => setFormMessage(e.target.value)} placeholder="Any notes for the contractor?" />
          </Field>
          <div style={styles.modalActions}>
            <button disabled={submitting} onClick={() => setModal(null)} style={styles.secondaryBtn}>Cancel</button>
            <button disabled={submitting} onClick={() => postAction('approve', { approvedByName: formName || client?.name, message: formMessage })} style={styles.primaryBtn}>
              {submitting ? 'Approving…' : 'Confirm approval'}
            </button>
          </div>
        </Modal>
      )}
      {modal === 'reject' && (
        <Modal title="Reject quote" onClose={() => setModal(null)}>
          <Field label="Reason (optional)">
            <textarea style={{ ...styles.input, minHeight: 100 }} value={formMessage} onChange={(e) => setFormMessage(e.target.value)} placeholder="Help the contractor understand why" />
          </Field>
          <div style={styles.modalActions}>
            <button disabled={submitting} onClick={() => setModal(null)} style={styles.secondaryBtn}>Cancel</button>
            <button disabled={submitting} onClick={() => postAction('reject', { reason: formMessage })} style={{ ...styles.primaryBtn, background: '#b91c1c' }}>
              {submitting ? 'Submitting…' : 'Reject quote'}
            </button>
          </div>
        </Modal>
      )}
      {modal === 'changes' && (
        <Modal title="Request changes" onClose={() => setModal(null)}>
          <Field label="What changes would you like?">
            <textarea style={{ ...styles.input, minHeight: 120 }} value={formMessage} onChange={(e) => setFormMessage(e.target.value)} placeholder="e.g. Please remove item 3 and add an extra radiator…" />
          </Field>
          <div style={styles.modalActions}>
            <button disabled={submitting} onClick={() => setModal(null)} style={styles.secondaryBtn}>Cancel</button>
            <button
              disabled={submitting || !formMessage.trim()}
              onClick={() => postAction('request-changes', { message: formMessage })}
              style={styles.primaryBtn}
            >
              {submitting ? 'Sending…' : 'Send request'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f3f4f6', padding: 24, fontFamily: systemFont }}>
      <div style={{ background: '#fff', padding: 32, borderRadius: 12, maxWidth: 420, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>{children}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: QuoteData['quote']['status'] }) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    SENT: { bg: '#dbeafe', fg: '#1e40af', label: 'Awaiting your decision' },
    ACCEPTED: { bg: '#d1fae5', fg: '#065f46', label: 'Approved' },
    REJECTED: { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
    CHANGES_REQUESTED: { bg: '#fef3c7', fg: '#92400e', label: 'Changes requested' },
    DRAFT: { bg: '#e5e7eb', fg: '#374151', label: 'Draft' },
    EXPIRED: { bg: '#e5e7eb', fg: '#374151', label: 'Expired' },
  };
  const s = map[status] || map.SENT;
  return (
    <span style={{ background: s.bg, color: s.fg, padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

function Banner({ tone, title, body }: { tone: 'success' | 'muted' | 'info'; title: string; body: string }) {
  const colors = {
    success: { bg: '#ecfdf5', border: '#a7f3d0', fg: '#065f46' },
    muted:   { bg: '#f9fafb', border: '#e5e7eb', fg: '#374151' },
    info:    { bg: '#eff6ff', border: '#bfdbfe', fg: '#1e40af' },
  }[tone];
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16, color: colors.fg, marginTop: 8 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
      <div>{body}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</div>
      {children}
    </label>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'grid', placeItems: 'center', padding: 16, zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 520, width: '100%', padding: 24, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18, color: '#111827' }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

const systemFont = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const styles: Record<string, React.CSSProperties> = {
  page: { background: '#f3f4f6', minHeight: '100vh', padding: '24px 16px 64px', fontFamily: systemFont, color: '#111827' },
  wrap: { maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' },
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  label: { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#6b7280', textTransform: 'uppercase' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 10, fontSize: 14 },
  th: { textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' },
  td: { padding: '10px 8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  primaryBtn: { background: '#1d4ed8', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  secondaryBtn: { background: '#fff', color: '#111827', border: '1px solid #d1d5db', padding: '10px 18px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14, textDecoration: 'none', display: 'inline-block' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, fontFamily: systemFont, boxSizing: 'border-box' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
};
